<script>
  import { onMount } from 'svelte';
  import { fetchIvivoOrders, subscribeToIvivoOrders, unsubscribeFromIvivoOrders, exportIvivoOrders } from '../controllers/ivivoOrdersController.js';
  import * as XLSX from 'xlsx';

  let ivivoOrders = [];
  let totalRecords = 0;
  let isLoading = true;
  let error = null;
  let processed = '';
  let startDate = '';
  let endDate = '';
  let page = 1;
  let perPage = 10;
  let totalPages = 1;

  // Default: last 2 days including today
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const todayStr = today.toISOString().slice(0, 10);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const yesterdayStr = yesterday.toISOString().slice(0, 10);
  startDate = yesterdayStr;
  endDate = todayStr;

  async function loadOrders() {
    isLoading = true;
    error = null;
    try {
      const result = await fetchIvivoOrders({
        page,
        perPage,
        processed: processed === '' ? null : processed === 'true',
        startDate,
        endDate
      });
      if (result) {
        ivivoOrders = result.ivivoOrders;
        totalRecords = result.totalRecords;
        totalPages = Math.ceil(result.totalRecords / perPage) || 1;
      }
    } catch (err) {
      error = 'Error al cargar los resultados de iVivo.';
    } finally {
      isLoading = false;
    }
  }

  function handleProcessedChange(e) {
    processed = e.target.value;
  }
  function handleStartDateChange(e) {
    startDate = e.target.value;
  }
  function handleEndDateChange(e) {
    endDate = e.target.value;
  }
  function handleFilter() {
    page = 1;
    loadOrders();
  }
  function handleClearFilters() {
    processed = '';
    startDate = yesterdayStr;
    endDate = todayStr;
    page = 1;
    loadOrders();
  }
  function goToPage(p) {
    if (p >= 1 && p <= totalPages) {
      page = p;
      loadOrders();
    }
  }

  async function handleExportExcel() {
    isLoading = true;
    try {
      const allOrders = await exportIvivoOrders({
        processed: processed === '' ? null : processed === 'true',
        startDate,
        endDate
      });
      if (!allOrders) throw new Error('No se pudieron exportar los datos.');
      // Prepare data for Excel
      const data = allOrders.map(order => ({
        orderDate: order.orderDate || '',
        processed: order.processed ? 'Sí' : 'No',
        total: order.total ?? '',
        totalItems: order.totalItems ?? '',
        ecwidOrder: order.ecwidOrder ?? '',
        ivivoId: order.ivivoId ?? '',
        apiResponseDate: order.apiResponseDate || '',
        statusCode: order.statusCode ?? '',
        message: order.message ?? '',
        rawResponse: order.rawResponse ? JSON.stringify(order.rawResponse) : ''
      }));
      // Column order and headers as per image
      const ws = XLSX.utils.json_to_sheet(data, {
        header: [
          'orderDate', 'processed', 'total', 'totalItems', 'ecwidOrder', 'ivivoId', 'apiResponseDate', 'statusCode', 'message', 'rawResponse'
        ]
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'ResultadosIVivo');
      XLSX.writeFile(wb, `ResultadosIVivo_${startDate}_a_${endDate}.xlsx`);
    } catch (err) {
      alert('Error al exportar a Excel.');
    } finally {
      isLoading = false;
    }
  }

  onMount(() => {
    loadOrders();
    subscribeToIvivoOrders(loadOrders);
    return () => unsubscribeFromIvivoOrders();
  });

  // Dashboard summary
  $: processedCount = ivivoOrders.filter(o => o.processed).length;
  $: unprocessedCount = ivivoOrders.filter(o => !o.processed).length;
  $: totalAmount = ivivoOrders.reduce((sum, o) => sum + (o.total || 0), 0);
  $: totalItems = ivivoOrders.reduce((sum, o) => sum + (o.totalItems || 0), 0);
</script>

<div class="relative md:pt-6 pb-16 flex justify-between items-center">
    <h2 class="text-white text-lg font-bold">Resultados iVivo</h2>
</div>
<!-- Improved Filter Bar -->
<div class="w-full bg-white shadow rounded-lg p-4 mb-6 flex flex-wrap items-center gap-4">
  <div>
    <label class="font-semibold mr-2">Procesado:</label>
    <select bind:value={processed} on:change={handleProcessedChange} class="border rounded px-3 py-2">
      <option value="">Todos</option>
      <option value="true">Sí</option>
      <option value="false">No</option>
    </select>
  </div>
  <div>
    <label class="font-semibold mr-2">Fecha inicial:</label>
    <input type="date" bind:value={startDate} max={endDate} on:change={handleStartDateChange} class="border rounded px-3 py-2" />
  </div>
  <div>
    <label class="font-semibold mr-2">Fecha final:</label>
    <input type="date" bind:value={endDate} min={startDate} max={todayStr} on:change={handleEndDateChange} class="border rounded px-3 py-2" />
  </div>
  <button on:click={handleFilter} class="ml-4 bg-blueGray-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center transition-all duration-200">
    <i class="fas fa-search mr-2"></i> Filtrar
  </button>
  <button on:click={handleClearFilters} class="ml-2 bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 transition-all duration-200">
    Limpiar filtros
  </button>
  <button on:click={handleExportExcel} class="ml-2 bg-blueGray-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-all duration-200 flex items-center">
    <i class="fas fa-file-excel mr-2"></i> Exportar Excel
  </button>
  <div class="ml-auto text-right">
    <span class="font-semibold">Total registros:</span> {totalRecords}
  </div>
</div>

<!-- Horizontal Summary Bar -->
<div class="w-full flex flex-col md:flex-row gap-4 mb-6">
  <div class="flex-1 bg-gradient-to-r from-green-400 to-green-200 rounded-lg shadow p-4 flex flex-col items-center justify-center">
    <div class="text-2xl font-bold">{processedCount}</div>
    <div class="text-gray-700 font-semibold">Procesados</div>
  </div>
  <div class="flex-1 bg-gradient-to-r from-yellow-300 to-yellow-100 rounded-lg shadow p-4 flex flex-col items-center justify-center">
    <div class="text-2xl font-bold">{unprocessedCount}</div>
    <div class="text-gray-700 font-semibold">No procesados</div>
  </div>
  <div class="flex-1 bg-gradient-to-r from-blue-400 to-blue-200 rounded-lg shadow p-4 flex flex-col items-center justify-center">
    <div class="text-2xl font-bold">{totalAmount.toLocaleString()}</div>
    <div class="text-gray-700 font-semibold">Total monto</div>
  </div>
  <div class="flex-1 bg-gradient-to-r from-purple-400 to-purple-200 rounded-lg shadow p-4 flex flex-col items-center justify-center">
    <div class="text-2xl font-bold">{totalItems}</div>
    <div class="text-gray-700 font-semibold">Total items</div>
  </div>
</div>

<!-- Responsive Table -->
<div class="overflow-x-auto rounded-lg shadow border border-gray-200 bg-white">
  <table class="min-w-full divide-y divide-gray-200">
    <thead class="bg-gray-50">
      <tr>
        <th class="px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wider">orderDate</th>
        <th class="px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wider">processed</th>
        <th class="px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wider">total</th>
        <th class="px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wider">totalItems</th>
        <th class="px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wider">ecwidOrder</th>
        <th class="px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wider">ivivoId</th>
        <th class="px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wider">apiResponseDate</th>
        <th class="px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wider">statusCode</th>
        <th class="px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wider">message</th>
        <th class="px-3 py-2 text-xs font-bold text-gray-700 uppercase tracking-wider">rawResponse</th>
      </tr>
    </thead>
    <tbody class="divide-y divide-gray-100">
      {#if isLoading}
        <tr><td colspan="10" class="text-center py-6 text-blue-500 animate-pulse">Cargando...</td></tr>
      {:else if error}
        <tr><td colspan="10" class="text-center text-red-600 py-6">{error}</td></tr>
      {:else if ivivoOrders.length === 0}
        <tr><td colspan="10" class="text-center py-6">Sin resultados</td></tr>
      {:else}
        {#each ivivoOrders as order}
          <tr class="hover:bg-blue-50 transition-colors duration-150">
            <td class="px-3 py-2 whitespace-nowrap text-sm">{order.orderDate}</td>
            <td class="px-3 py-2 whitespace-nowrap text-sm">{order.processed ? 'Sí' : 'No'}</td>
            <td class="px-3 py-2 whitespace-nowrap text-sm">{order.total}</td>
            <td class="px-3 py-2 whitespace-nowrap text-sm">{order.totalItems}</td>
            <td class="px-3 py-2 whitespace-nowrap text-sm">{order.ecwidOrder}</td>
            <td class="px-3 py-2 whitespace-nowrap text-sm">{order.ivivoId}</td>
            <td class="px-3 py-2 whitespace-nowrap text-sm">{order.apiResponseDate}</td>
            <td class="px-3 py-2 whitespace-nowrap text-sm">{order.statusCode}</td>
            <td class="px-3 py-2 whitespace-nowrap text-xs max-w-xs overflow-x-auto">{order.message}</td>
            <td class="px-3 py-2 whitespace-pre-wrap text-xs max-w-xs overflow-x-auto"><pre>{order.rawResponse ? JSON.stringify(order.rawResponse, null, 2) : ''}</pre></td>
          </tr>
        {/each}
      {/if}
    </tbody>
  </table>
</div>

<!-- Pagination -->
{#if totalPages > 1}
  <div class="flex flex-wrap justify-center items-center gap-2 mt-6">
    <button on:click={() => goToPage(page - 1)} class="px-3 py-2 rounded-l bg-gray-200 hover:bg-blue-200 text-gray-700 font-bold transition-all duration-150" disabled={page === 1}>&laquo;</button>
    {#each Array(totalPages) as _, i}
      <button on:click={() => goToPage(i + 1)} class="px-3 py-2 rounded bg-white border border-gray-300 hover:bg-blue-100 text-blue-700 font-semibold transition-all duration-150 {page === i + 1 ? 'bg-blue-500 text-white' : ''}">{i + 1}</button>
    {/each}
    <button on:click={() => goToPage(page + 1)} class="px-3 py-2 rounded-r bg-gray-200 hover:bg-blue-200 text-gray-700 font-bold transition-all duration-150" disabled={page === totalPages}>&raquo;</button>
  </div>
{/if}

<style>
  @media (max-width: 768px) {
    table {
      font-size: 0.85rem;
    }
    th, td {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      white-space: normal;
      word-break: break-word;
    }
    .max-w-xs {
      max-width: 120px;
    }
    .flex-col.md\:flex-row {
      flex-direction: column !important;
    }
  }
  /* Smooth horizontal scroll for table */
  .overflow-x-auto {
    scroll-behavior: smooth;
  }
</style> 